import { Component, inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import {
  DictionaryEntryModel,
  DictionaryKey,
  extractNumber,
  HxDictionaryService,
  HxReportService,
  HxSpendService,
  HxUiLabelPipe,
  SpendingFullModel,
  UiLabel
} from 'hx-services';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { HxDateSelectComponent, HxStoreSelectComponent } from 'hx-component';
import { SpendCategorySearchComponent } from '../spend-category-search/spend-category-search.component';

@Component({
    selector: 'app-spend-update',
    templateUrl: './spend-update.component.html',
    styleUrls: ['./spend-update.component.css'],
  imports: [RouterLink, FormsModule, ReactiveFormsModule, HxStoreSelectComponent, SpendCategorySearchComponent, HxDateSelectComponent, TranslocoPipe, HxUiLabelPipe]
})
export class SpendUpdateComponent implements OnInit {

  form = new FormGroup({
    spendCategoryCode: new FormControl<string | null>(null, Validators.required),
    storeId: new FormControl<number | null>(null, Validators.required),
    total: new FormControl<number | null>(null, Validators.required),
    date: new FormControl<string | null>(null, Validators.required),
    incomingNumber: new FormControl<string | null>(null),
    payBefore: new FormControl<string | null>(null, Validators.required),
  });
  isLoading = {
    save: false,
    entries: false,
  };
  spending?: SpendingFullModel;
  cashFlowCategory?: UiLabel;
  cashFlowCategoryCode?: string;
  private map = new Map<string, UiLabel>;

  private readonly spendService = inject(HxSpendService);
  private readonly aRoute = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly toastr = inject(ToastrService);
  private readonly tr = inject(TranslocoService);
  private readonly dictionaryService = inject(HxDictionaryService);

  constructor(
  ) {
    this.loadDictionaryEntries();
  }

  async ngOnInit() {
    const id = extractNumber('id', this.aRoute.snapshot.paramMap);
    this.spending = await this.spendService.getSpendById(id);

    const formObj = {
      spendCategoryCode: undefined,
      storeId: undefined,
      total: undefined,
      date: undefined,
      incomingNumber: undefined,
      payBefore: undefined,
    };

    formObj.total = this.spending.total;
    formObj.incomingNumber = this.spending.incomingNumber;

    if (this.spending.store.id) {
      formObj.storeId = this.spending.store.id;
    }

    formObj.spendCategoryCode = this.spending.spendCategory.code;
    formObj.date = this.spending.date;
    formObj.payBefore = this.spending.payBefore;

    this.dictionaryService.getEntryByCode(this.spending.spendCategory.attributes.code).then(entry => {
      this.cashFlowCategoryCode = entry.code;
      this.cashFlowCategory = entry.title;
    });

    this.form.patchValue(formObj);
  }

  async onSubmitForm() {
    Object.values(this.form.controls).forEach(ctrl => {
      ctrl.markAsDirty();
      ctrl.updateValueAndValidity();
    });
    if (this.form.valid) {
      const formModel = this.form.value;
      this.isLoading.save = true;
      try {
        await this.spendService.saveSpendStore({
          id: this.spending?.id,
          spendCategoryCode: formModel.spendCategoryCode,
          storeId: formModel.storeId,
          total: formModel.total,
          date: formModel.date,
          incomingNumber: formModel.incomingNumber,
          payBefore: formModel.payBefore
        });
        this.toastr.success(this.tr.translate('hx.nt.saved'));
        await this.router.navigate(['../'], {relativeTo: this.aRoute});
      } finally {
        this.isLoading.save = false;
      }
    } else {
      this.toastr.warning(this.tr.translate('hx.form.fillRequiredFields'));
    }
  }

  cancel() {
    this.router.navigate(['../'], {
      relativeTo: this.aRoute
    });
  }

  onSpendCategorySelected(category?: DictionaryEntryModel) {
    if (category) {
      this.form.controls.spendCategoryCode.patchValue(category.code);
      this.cashFlowCategory = this.map.get(category.attributes.code);
      this.cashFlowCategoryCode = category.attributes.code;
    } else {
      this.form.controls.spendCategoryCode.patchValue(undefined);
      this.cashFlowCategory = undefined;
      this.cashFlowCategoryCode = undefined;
    }
  }

  private async loadDictionaryEntries() {
    this.isLoading.entries = true;
    try {
      const entries = await this.dictionaryService.getEntries({ key: DictionaryKey.cashFlows, hierarchy: false});

      entries.forEach(item => {
        this.map.set(item.code, item.title);
      });
    } finally {
      this.isLoading.entries = false;
    }
  }
}
